import * as THREE from "three";
import {
  createCloudTexture,
  createMoonTexture,
  createSunTexture,
} from "./sky-textures";

/**
 * Sky dome + Minecraft celestial system:
 * - Authentic Minecraft sky gradient with sunrise/sunset horizon glow and void floor
 * - Rotating celestial group with square Sun (golden corona) and square Moon (crater details)
 * - 1,500 twinkling stars across the night hemisphere that fade in at dusk and out at dawn
 * - Procedural Minecraft cloud layer at Y=128 drifting continuously with wind and tracking player
 */

const DAY_LENGTH = 600; // seconds for a full 24-hour cycle
const STAR_COUNT = 1500;
const CLOUD_ALTITUDE = 128;
const CLOUD_WORLD_SIZE = 256 * 12; // 256 pixels @ 12 blocks/pixel = 3072 blocks
const CLOUD_PLANE_SIZE = 1024; // 1024x1024 plane around player

/** Scratch colour for keyframe interpolation. */
const _tmpColor = new THREE.Color();

interface SkyKeyframe {
  /** 0..1 through the day; 0 is sunrise, 0.5 is sunset. */
  t: number;
  zenith: number;
  horizon: number;
  light: number;
  fog: number;
}

const KEYS: readonly SkyKeyframe[] = [
  { t: 0.0, zenith: 0x050812, horizon: 0x0c1220, light: 0.16, fog: 0x080c18 },
  { t: 0.22, zenith: 0x0d1730, horizon: 0x2b3350, light: 0.28, fog: 0x1b2138 },
  { t: 0.27, zenith: 0x485888, horizon: 0xff7a38, light: 0.72, fog: 0xd06c40 },
  { t: 0.32, zenith: 0x6e9fff, horizon: 0xc8deff, light: 0.95, fog: 0xc0d8ff },
  { t: 0.5, zenith: 0x78a7ff, horizon: 0xc0d8ff, light: 1.0, fog: 0xc0d8ff },
  { t: 0.68, zenith: 0x6e9fff, horizon: 0xc8deff, light: 0.95, fog: 0xc0d8ff },
  { t: 0.73, zenith: 0x485888, horizon: 0xff6628, light: 0.68, fog: 0xc85530 },
  { t: 0.78, zenith: 0x0d1730, horizon: 0x2b3350, light: 0.28, fog: 0x1b2138 },
  { t: 1.0, zenith: 0x050812, horizon: 0x0c1220, light: 0.16, fog: 0x080c18 },
];

const VERT = /* glsl */ `
  varying vec3 vDir;
  void main() {
    vDir = normalize(position);
    // Drop the translation from the view matrix so the dome follows the camera at infinity.
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    mv.z = 0.0;
    gl_Position = projectionMatrix * mv;
  }
`;

const FRAG = /* glsl */ `
  uniform vec3 uZenith;
  uniform vec3 uHorizon;
  uniform vec3 uVoidColor;
  uniform vec3 uSunDir;
  varying vec3 vDir;

  void main() {
    vec3 d = normalize(vDir);

    // 1. Sky Gradient:
    vec3 col;
    if (d.y >= 0.0) {
      // Horizon band blending up into zenith
      float h = pow(1.0 - d.y, 2.4);
      col = mix(uZenith, uHorizon, h * 0.88);
    } else {
      // Below horizon: dark void floor
      float v = clamp(-d.y * 3.5, 0.0, 1.0);
      col = mix(uHorizon, uVoidColor, v);
    }

    // 2. Sunset / Sunrise Golden Horizon Glow:
    float sunElev = uSunDir.y;
    if (abs(sunElev) < 0.38) {
      vec3 sunHorizDir = normalize(vec3(uSunDir.x, 0.0, uSunDir.z));
      vec3 viewHorizDir = normalize(vec3(d.x, 0.0, d.z));
      float sunAlign = max(0.0, dot(viewHorizDir, sunHorizDir));
      float horizonFactor = smoothstep(-0.06, 0.16, d.y) * smoothstep(0.45, 0.0, d.y);
      float glowStrength = smoothstep(0.38, 0.0, abs(sunElev)) * pow(sunAlign, 2.8);
      vec3 glowColor = mix(vec3(1.0, 0.48, 0.16), vec3(0.92, 0.22, 0.32), smoothstep(0.0, -0.2, sunElev));
      col = mix(col, glowColor, glowStrength * horizonFactor * 0.88);
    }

    gl_FragColor = vec4(col, 1.0);
    #include <colorspace_fragment>
  }
`;

const CELESTIAL_DISTANCE = 70;

const CELESTIAL_VERT = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vWorldDir;

  void main() {
    vUv = uv;
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    // Camera-relative direction vector to check elevation above the horizon
    vWorldDir = normalize(worldPos.xyz - cameraPosition);
    vec4 p = projectionMatrix * viewMatrix * worldPos;
    // Force depth to far plane (0.99999) so it is behind all terrain blocks
    // and can never be sliced by the camera far plane regardless of crosshair aim
    gl_Position = vec4(p.x, p.y, p.w * 0.99999, p.w);
  }
`;

const CELESTIAL_FRAG = /* glsl */ `
  uniform sampler2D uMap;
  varying vec2 vUv;
  varying vec3 vWorldDir;

  void main() {
    // Horizon cutoff: smoothly disappear as the body sinks below the horizon
    float horizon = smoothstep(-0.015, 0.005, vWorldDir.y);
    if (horizon <= 0.0) discard;

    vec4 tex = texture2D(uMap, vUv);
    tex.a *= horizon;
    if (tex.a <= 0.01) discard;

    gl_FragColor = tex;
    #include <colorspace_fragment>
  }
`;

const STARS_VERT = /* glsl */ `
  attribute vec3 color;
  varying vec3 vColor;
  varying vec3 vWorldDir;
  uniform float uSize;

  void main() {
    vColor = color;
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vWorldDir = normalize(worldPos.xyz - cameraPosition);
    gl_PointSize = uSize;
    vec4 p = projectionMatrix * viewMatrix * worldPos;
    gl_Position = vec4(p.x, p.y, p.w * 0.99999, p.w);
  }
`;

const STARS_FRAG = /* glsl */ `
  uniform float uOpacity;
  varying vec3 vColor;
  varying vec3 vWorldDir;

  void main() {
    // Stars only appear above the horizon
    float horizon = smoothstep(-0.005, 0.015, vWorldDir.y);
    float a = uOpacity * horizon;
    if (a <= 0.01) discard;

    gl_FragColor = vec4(vColor, a);
    #include <colorspace_fragment>
  }
`;

export class Sky {
  /** Root group added to the engine scene; contains dome, celestials, and clouds. */
  readonly mesh: THREE.Group;

  /** 0..1 around the cycle. 0.5 is high noon. */
  time = 0.34;
  readonly sunDirection = new THREE.Vector3(0, 1, 0);

  private readonly domeMesh: THREE.Mesh;
  private readonly domeGeo: THREE.SphereGeometry;
  private readonly domeMat: THREE.ShaderMaterial;

  private readonly celestialGroup: THREE.Group;

  private readonly sunMesh: THREE.Mesh;
  private readonly sunGeo: THREE.PlaneGeometry;
  private readonly sunMat: THREE.ShaderMaterial;
  private readonly sunTexture: THREE.DataTexture;

  private readonly moonMesh: THREE.Mesh;
  private readonly moonGeo: THREE.PlaneGeometry;
  private readonly moonMat: THREE.ShaderMaterial;
  private readonly moonTexture: THREE.DataTexture;

  private readonly starsPoints: THREE.Points;
  private readonly starsGeo: THREE.BufferGeometry;
  private readonly starsMat: THREE.ShaderMaterial;

  private readonly cloudsMesh: THREE.Mesh;
  private readonly cloudsGeo: THREE.PlaneGeometry;
  private readonly cloudsMat: THREE.MeshBasicMaterial;
  private readonly cloudTexture: THREE.DataTexture;

  private readonly zenith = new THREE.Color();
  private readonly horizon = new THREE.Color();
  private readonly voidColor = new THREE.Color(0x06080d);
  private readonly fog = new THREE.Color();
  private readonly underwaterFog = new THREE.Color(0x081830);

  private cloudScroll = 0;
  private twinkleTime = 0;

  constructor() {
    this.mesh = new THREE.Group();
    this.mesh.name = "sky-root";

    // --- 1. Background Sky Dome ---
    this.domeMat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
      fog: false,
      uniforms: {
        uZenith: { value: new THREE.Color(KEYS[4].zenith) },
        uHorizon: { value: new THREE.Color(KEYS[4].horizon) },
        uVoidColor: { value: this.voidColor },
        uSunDir: { value: this.sunDirection },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
    });
    this.domeGeo = new THREE.SphereGeometry(1, 32, 16);
    this.domeMesh = new THREE.Mesh(this.domeGeo, this.domeMat);
    this.domeMesh.frustumCulled = false;
    this.domeMesh.renderOrder = -1000;
    this.mesh.add(this.domeMesh);

    // --- 2. Rotating Celestial Group (Sun, Moon, Stars) ---
    this.celestialGroup = new THREE.Group();
    this.celestialGroup.name = "celestials";
    this.mesh.add(this.celestialGroup);

    // Square Sun: scaled to 11.2x11.2 at distance 70 (identical angular size to 16x16 at 100)
    // Placed at +X (sunrise position) facing origin, rotates around Z with time
    this.sunTexture = createSunTexture();
    this.sunGeo = new THREE.PlaneGeometry(11.2, 11.2);
    this.sunMat = new THREE.ShaderMaterial({
      uniforms: {
        uMap: { value: this.sunTexture },
      },
      vertexShader: CELESTIAL_VERT,
      fragmentShader: CELESTIAL_FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      fog: false,
    });
    this.sunMesh = new THREE.Mesh(this.sunGeo, this.sunMat);
    this.sunMesh.position.set(CELESTIAL_DISTANCE, 0, 0);
    this.sunMesh.lookAt(0, 0, 0);
    this.sunMesh.frustumCulled = false;
    this.sunMesh.renderOrder = -900;
    this.celestialGroup.add(this.sunMesh);

    // Square Moon with Craters (180 deg opposite Sun at -X)
    this.moonTexture = createMoonTexture();
    this.moonGeo = new THREE.PlaneGeometry(9.8, 9.8);
    this.moonMat = new THREE.ShaderMaterial({
      uniforms: {
        uMap: { value: this.moonTexture },
      },
      vertexShader: CELESTIAL_VERT,
      fragmentShader: CELESTIAL_FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      fog: false,
    });
    this.moonMesh = new THREE.Mesh(this.moonGeo, this.moonMat);
    this.moonMesh.position.set(-CELESTIAL_DISTANCE, 0, 0);
    this.moonMesh.lookAt(0, 0, 0);
    this.moonMesh.frustumCulled = false;
    this.moonMesh.renderOrder = -900;
    this.celestialGroup.add(this.moonMesh);

    // 1,500 Twinkling Stars placed at distance 75
    this.starsGeo = new THREE.BufferGeometry();
    const starPositions = new Float32Array(STAR_COUNT * 3);
    const starColors = new Float32Array(STAR_COUNT * 3);
    let rng = 987654321;
    const nextRng = (): number => {
      rng = (rng * 1664525 + 1013904223) >>> 0;
      return rng / 4294967296;
    };
    for (let i = 0; i < STAR_COUNT; i++) {
      const u = nextRng();
      const v = nextRng();
      const theta = u * 2.0 * Math.PI;
      const phi = Math.acos(2.0 * v - 1.0);
      const r = 75.0;
      starPositions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      starPositions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      starPositions[i * 3 + 2] = r * Math.cos(phi);

      const tint = nextRng();
      if (tint < 0.65) {
        starColors[i * 3] = 1.0;
        starColors[i * 3 + 1] = 1.0;
        starColors[i * 3 + 2] = 1.0;
      } else if (tint < 0.85) {
        starColors[i * 3] = 0.88;
        starColors[i * 3 + 1] = 0.94;
        starColors[i * 3 + 2] = 1.0;
      } else {
        starColors[i * 3] = 1.0;
        starColors[i * 3 + 1] = 0.96;
        starColors[i * 3 + 2] = 0.84;
      }
    }
    this.starsGeo.setAttribute(
      "position",
      new THREE.BufferAttribute(starPositions, 3),
    );
    this.starsGeo.setAttribute("color", new THREE.BufferAttribute(starColors, 3));
    this.starsMat = new THREE.ShaderMaterial({
      uniforms: {
        uSize: { value: 1.8 },
        uOpacity: { value: 0.0 },
      },
      vertexShader: STARS_VERT,
      fragmentShader: STARS_FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      fog: false,
    });
    this.starsPoints = new THREE.Points(this.starsGeo, this.starsMat);
    this.starsPoints.frustumCulled = false;
    this.starsPoints.renderOrder = -950;
    this.celestialGroup.add(this.starsPoints);

    // --- 3. Authentic Minecraft Clouds Layer at Y=128 ---
    this.cloudTexture = createCloudTexture();
    this.cloudsGeo = new THREE.PlaneGeometry(
      CLOUD_PLANE_SIZE,
      CLOUD_PLANE_SIZE,
      1,
      1,
    );
    this.cloudsMat = new THREE.MeshBasicMaterial({
      map: this.cloudTexture,
      transparent: true,
      opacity: 0.70,
      side: THREE.DoubleSide,
      depthWrite: false,
      fog: true,
    });
    this.cloudsMesh = new THREE.Mesh(this.cloudsGeo, this.cloudsMat);
    this.cloudsMesh.name = "clouds";
    this.cloudsMesh.rotation.x = -Math.PI / 2;
    this.cloudsMesh.position.set(0, CLOUD_ALTITUDE, 0);
    this.cloudsMesh.renderOrder = -850;
    this.cloudTexture.repeat.set(
      CLOUD_PLANE_SIZE / CLOUD_WORLD_SIZE,
      CLOUD_PLANE_SIZE / CLOUD_WORLD_SIZE,
    );
    this.mesh.add(this.cloudsMesh);
  }

  /** Advance clock and sync celestial & cloud transforms. */
  update(dt: number, playerPos?: THREE.Vector3): void {
    this.time = (this.time + dt / DAY_LENGTH) % 1;

    // Celestial angle: sunrise at t=0.25 (angle=0, sun at +X), noon at t=0.5 (angle=PI/2, sun at +Y zenith), sunset at t=0.75 (angle=PI, sun at -X)
    const angle = (this.time - 0.25) * Math.PI * 2;
    this.sunDirection.set(Math.cos(angle), Math.sin(angle), 0.15).normalize();

    // Celestials rotate with time and follow player/camera
    if (playerPos) {
      this.celestialGroup.position.copy(playerPos);
    }
    this.celestialGroup.rotation.z = angle;
    this.celestialGroup.rotation.y = -0.15;

    // Drifting clouds follow player in X/Z and scroll with wind
    this.cloudScroll += dt * 0.75;
    this.twinkleTime += dt * 3.5;

    const px = playerPos?.x ?? 0;
    const pz = playerPos?.z ?? 0;
    this.cloudsMesh.position.set(px, CLOUD_ALTITUDE, pz);
    this.cloudTexture.offset.set(
      (px + this.cloudScroll) / CLOUD_WORLD_SIZE,
      pz / CLOUD_WORLD_SIZE,
    );
  }

  /** True through the dark part of the cycle, dusk to dawn. */
  get isNight(): boolean {
    return this.time >= 0.78 || this.time < 0.22;
  }

  get isDay(): boolean {
    return !this.isNight;
  }

  /** Interpolate keyframes and update shader uniforms, cloud colors, and star opacity. */
  sample(): { light: number; fog: THREE.Color } {
    let i = 0;
    while (i < KEYS.length - 2 && KEYS[i + 1].t < this.time) i++;
    const a = KEYS[i];
    const b = KEYS[i + 1];
    const span = b.t - a.t || 1;
    const f = THREE.MathUtils.clamp((this.time - a.t) / span, 0, 1);
    const s = f * f * (3 - 2 * f);

    this.zenith.setHex(a.zenith).lerp(_tmpColor.setHex(b.zenith), s);
    this.horizon.setHex(a.horizon).lerp(_tmpColor.setHex(b.horizon), s);
    this.fog.setHex(a.fog).lerp(_tmpColor.setHex(b.fog), s);
    const light = a.light + (b.light - a.light) * s;

    this.domeMat.uniforms.uZenith.value.copy(this.zenith);
    this.domeMat.uniforms.uHorizon.value.copy(this.horizon);

    // Stars fade in at dusk, shine at night, fade out at dawn
    const sunElev = this.sunDirection.y;
    const nightProgress = THREE.MathUtils.clamp(
      (0.15 - sunElev) / 0.35,
      0,
      1,
    );
    const twinkle = 0.90 + 0.10 * Math.sin(this.twinkleTime);
    this.starsMat.uniforms.uOpacity.value = nightProgress * twinkle;

    // Shading clouds to match time of day:
    if (this.isNight) {
      this.cloudsMat.color.setRGB(0.24, 0.28, 0.40);
      this.cloudsMat.opacity = 0.65;
    } else {
      const sunsetFactor = THREE.MathUtils.clamp((0.35 - Math.abs(sunElev)) / 0.35, 0, 1);
      if (sunsetFactor > 0.05) {
        // Warm golden-pink sunrise/sunset tint on clouds
        _tmpColor.setRGB(1.0, 0.76, 0.62);
        this.cloudsMat.color.copy(_tmpColor);
        this.cloudsMat.opacity = 0.75;
      } else {
        this.cloudsMat.color.setRGB(1.0, 1.0, 1.0);
        this.cloudsMat.opacity = 0.70;
      }
    }

    return { light, fog: this.fog };
  }

  fogColorFor(underwater: boolean): THREE.Color {
    return underwater ? this.underwaterFog : this.fog;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.domeGeo.dispose();
    this.domeMat.dispose();
    this.sunGeo.dispose();
    this.sunMat.dispose();
    this.sunTexture.dispose();
    this.moonGeo.dispose();
    this.moonMat.dispose();
    this.moonTexture.dispose();
    this.starsGeo.dispose();
    this.starsMat.dispose();
    this.cloudsGeo.dispose();
    this.cloudsMat.dispose();
    this.cloudTexture.dispose();
  }
}

export { DAY_LENGTH };
